import { useMutation, useQuery } from 'react-query';
import parse from 'url-parse';
import { captureException } from '@sentry/react';
import { GET_SINGLE_THREAD } from '../constants/graphql/queries/thread';
import queryKeys from '../constants/queryKeys';
import { chatgenieAPI } from '../services/chatgenieAPI';
import useToast from './useToast';
import { reactiveVarClient } from '../providers/ApolloClientProvider/utils';
import { getConversationSource } from '../utils';

const createTicket = ({ data }) =>
  chatgenieAPI.instance({
    method: 'POST',
    url: `/tickets`,
    data
  });

const patchTicket = ({ id, data, params }) =>
  chatgenieAPI.instance({
    method: 'PATCH',
    url: `/tickets/${id}?${parse.qs.stringify(params)}`,
    data
  });

export const getTicket = variables => {
  const apolloClient = reactiveVarClient();
  return apolloClient
    .query({
      query: GET_SINGLE_THREAD(),
      variables,
      fetchPolicy: 'network-only'
    })
    .then(res => {
      const { thread } = res.data;

      if (!thread) {
        const error = new Error(
          "We haven't found this thread in our records yet. This could be because the PSA hasn't informed us yet."
        );

        captureException(error, {
          extra: {
            data: JSON.stringify(res)
          },
          tags: {
            threadId: variables.id
          }
        });

        throw error;
      }

      return thread;
    });
};

const fallbackValue = {};

const useTicket = ({
  ticketId,
  onCreateSuccess = () => {},
  onCreateError = () => {},
  onPatchSuccess = () => {},
  onPatchError = () => {}
}) => {
  const { toast } = useToast();

  const {
    data: ticket,
    isLoading,
    refetch,
    error
  } = useQuery(
    [queryKeys.GET_TICKET, ticketId],
    () =>
      getTicket({
        id: ticketId
      }),
    {
      enabled: Boolean(ticketId),
      onError() {
        const toastMessage =
          "We haven't found this thread in our records yet. This could be because the PSA hasn't informed us yet.";

        toast.error(toastMessage, {
          id: `error-${ticketId}`
        });
      },
      refetchOnMount: false,
      staleTime: 3 * 60 * 1000,
      refetchOnReconnect: true,
      refetchOnWindowFocus: true
    }
  );
  const createMutation = useMutation(
    ({ data }) =>
      createTicket({
        data: {
          ...data,
          conversation_source: getConversationSource()
        }
      }),
    {
      onSuccess: onCreateSuccess,
      onError: onCreateError
    }
  );
  const patchMutation = useMutation(patchTicket, {
    onSuccess: onPatchSuccess,
    onError: onPatchError
  });

  return {
    ticket: ticket || fallbackValue,
    refetch,
    createMutation,
    patchMutation,
    error,
    isLoading
  };
};

export default useTicket;
